/**
 * Currency utilities — powered by live Fixer.io rates via CountryContext.
 * All components should use useCountry() for rates and this hook for formatting.
 */
import { useCountry } from '../contexts/CountryContext';
import { convertPrice, formatPropertyPrice } from './propertyPrice';

const COUNTRY_CURRENCY = { UK: 'GBP', IN: 'INR', CA: 'CAD' };
const SYMBOL_MAP = { GBP: '£', INR: '₹', CAD: 'C$', USD: '$', EUR: '€' };

/**
 * Hook that provides currency formatting using live Fixer.io exchange rates.
 * Replaces the old useCurrencySettings that called /api/currency/rates.
 */
export const useCurrencySettings = () => {
  const { country, exchangeRates, fees } = useCountry();
  const currency = COUNTRY_CURRENCY[country] || 'GBP';
  const symbol = SYMBOL_MAP[currency] || '£';

  const formatPrice = (price, fromCurrency) => {
    if (!price && price !== 0) return `${symbol}0`;
    if (fromCurrency && fromCurrency !== currency) {
      const converted = convertPrice(price, fromCurrency, currency, exchangeRates);
      return formatPropertyPrice(converted, currency);
    }
    return formatPropertyPrice(price, currency);
  };

  return {
    currency,
    symbol,
    exchangeRates,
    formatPrice,
    tenantFee: fees?.tenant_fee || 0,
    buyerFee: fees?.buyer_verification_fee || 20,
  };
};

export default useCurrencySettings;
